import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { functions } from "@/firebase";

interface DailydataListItem {
  id: string;
  dateMillis?: number | null;
  date?: { seconds?: number; _seconds?: number } | null;
  total_sales?: number;
  google_sales?: number;
  yahoo_sales?: number;
  af_sales?: number;
  oth_sales?: number;
  drape_sales?: number;
  total_cost?: number;
  google_cost?: number;
  yahoo_cost?: number;
  af_cost?: number;
  oth_cost?: number;
  total_prof?: number;
  google_prof?: number;
  yahoo_prof?: number;
  af_prof?: number;
  oth_prof?: number;
  ss?: number;
  cart?: number;
  checkout?: number;
  cv?: number;
  cpa_cart?: number;
  cpa_cv?: number;
}

interface DailydataListResult {
  success: boolean;
  items: DailydataListItem[];
}

type PresetKey = "7days" | "14days" | "30days" | "thisMonth" | "lastMonth" | "custom";

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const formatYen = (value: number) => `¥${Math.round(value).toLocaleString("ja-JP")}`;
const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;
const formatPercent0 = (value: number) => `${Math.round(value * 100)}%`;

const safeDivide = (a: number, b: number) => (b ? a / b : 0);

const ROAS_MIN = 120;
const ROAS_MAX = 350;
const ROAS_HEALTH_COLORS = [
  "#ef4444",
  "#f97316",
  "#fb923c",
  "#f59e0b",
  "#eab308",
  "#84cc16",
  "#22c55e",
  "#14b8a6",
  "#06b6d4",
  "#38bdf8",
] as const;

const getRoasHealth = (roasRatio: number) => {
  const roasPercent = roasRatio * 100;
  const clampedPercent = Math.min(ROAS_MAX, Math.max(ROAS_MIN, roasPercent));
  const progress = (clampedPercent - ROAS_MIN) / (ROAS_MAX - ROAS_MIN);
  const level = Math.min(9, Math.max(0, Math.floor(progress * 10)));
  return {
    roasPercent,
    progress,
    level,
    color: ROAS_HEALTH_COLORS[level],
  };
};

const toDateMillis = (item: DailydataListItem) => {
  if (typeof item.dateMillis === "number") return item.dateMillis;
  const seconds = item.date?.seconds ?? item.date?._seconds;
  return typeof seconds === "number" ? seconds * 1000 : null;
};

const toJstDayStart = (date: Date) => {
  const jst = new Date(date.getTime() + JST_OFFSET_MS);
  return Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth(), jst.getUTCDate()) - JST_OFFSET_MS;
};

const getRangeByPreset = (preset: Exclude<PresetKey, "custom">) => {
  const todayStart = toJstDayStart(new Date());
  const todayJst = new Date(todayStart + JST_OFFSET_MS);

  if (preset === "7days") {
    return { start: todayStart - ONE_DAY_MS * 7, endExclusive: todayStart };
  }
  if (preset === "14days") {
    return { start: todayStart - ONE_DAY_MS * 14, endExclusive: todayStart };
  }
  if (preset === "30days") {
    return { start: todayStart - ONE_DAY_MS * 29, endExclusive: todayStart + ONE_DAY_MS };
  }
  if (preset === "thisMonth") {
    const start = Date.UTC(todayJst.getUTCFullYear(), todayJst.getUTCMonth(), 1) - JST_OFFSET_MS;
    const endExclusive = Date.UTC(todayJst.getUTCFullYear(), todayJst.getUTCMonth() + 1, 1) - JST_OFFSET_MS;
    return { start, endExclusive };
  }

  const start = Date.UTC(todayJst.getUTCFullYear(), todayJst.getUTCMonth() - 1, 1) - JST_OFFSET_MS;
  const endExclusive = Date.UTC(todayJst.getUTCFullYear(), todayJst.getUTCMonth(), 1) - JST_OFFSET_MS;
  return { start, endExclusive };
};


const toInputDate = (millis: number) => {
  const jstDate = new Date(millis + JST_OFFSET_MS);
  const y = jstDate.getUTCFullYear();
  const m = `${jstDate.getUTCMonth() + 1}`.padStart(2, "0");
  const d = `${jstDate.getUTCDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseInputDateToStartMillis = (value: string) => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return Date.UTC(year, month - 1, day) - JST_OFFSET_MS;
};

const getRangeByCustom = (startDate: string, endDate: string) => {
  const start = parseInputDateToStartMillis(startDate);
  const endStart = parseInputDateToStartMillis(endDate);
  if (start === null || endStart === null) {
    return getRangeByPreset("30days");
  }
  return {
    start,
    endExclusive: endStart + ONE_DAY_MS,
  };
};

export default function AnalysisPage() {
  const [items, setItems] = useState<DailydataListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState<PresetKey>("30days");
  const defaultRange = useMemo(() => getRangeByPreset("30days"), []);
  const [customStartDate, setCustomStartDate] = useState(() => toInputDate(defaultRange.start));
  const [customEndDate, setCustomEndDate] = useState(() => toInputDate(defaultRange.endExclusive - ONE_DAY_MS));

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const getDailydataListFn = httpsCallable(functions, "getDailydataList");
        const response = await getDailydataListFn();
        const payload = response.data as DailydataListResult;
        setItems(payload.items || []);
      } catch (error) {
        console.error("Failed to fetch dailydata list", error);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  const activeRange = useMemo(() => {
    if (preset === "custom") {
      return getRangeByCustom(customStartDate, customEndDate);
    }
    return getRangeByPreset(preset);
  }, [preset, customStartDate, customEndDate]);

  const filtered = useMemo(() => {
    const range = activeRange;
    return items
      .map((item) => ({ ...item, _dateMillis: toDateMillis(item) }))
      .filter((item) => typeof item._dateMillis === "number")
      .filter((item) => (item._dateMillis as number) >= range.start && (item._dateMillis as number) < range.endExclusive)
      .sort((a, b) => (a._dateMillis as number) - (b._dateMillis as number));
  }, [items, activeRange]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, row) => {
        acc.totalSales += Number(row.total_sales ?? 0);
        acc.googleSales += Number(row.google_sales ?? 0);
        acc.yahooSales += Number(row.yahoo_sales ?? 0);
        acc.afSales += Number(row.af_sales ?? 0);
        acc.otherSales += Number(row.oth_sales ?? 0);
        acc.totalCost += Number(row.total_cost ?? 0);
        acc.googleCost += Number(row.google_cost ?? 0);
        acc.yahooCost += Number(row.yahoo_cost ?? 0);
        acc.afCost += Number(row.af_cost ?? 0);
        acc.googleProf += Number(row.google_prof ?? 0);
        acc.yahooProf += Number(row.yahoo_prof ?? 0);
        acc.afProf += Number(row.af_prof ?? 0);
        acc.otherProf += Number(row.oth_prof ?? 0);
        acc.ss += Number(row.ss ?? 0);
        acc.cart += Number(row.cart ?? 0);
        acc.checkout += Number(row.checkout ?? 0);
        acc.cv += Number(row.cv ?? 0);
        acc.cpaCvTotal += Number(row.cpa_cv ?? 0);
        acc.cpaCartTotal += Number(row.cpa_cart ?? 0);
        return acc;
      },
      {
        totalSales: 0,
        googleSales: 0,
        yahooSales: 0,
        afSales: 0,
        otherSales: 0,
        totalCost: 0,
        googleCost: 0,
        yahooCost: 0,
        afCost: 0,
        googleProf: 0,
        yahooProf: 0,
        afProf: 0,
        otherProf: 0,
        ss: 0,
        cart: 0,
        checkout: 0,
        cv: 0,
        cpaCvTotal: 0,
        cpaCartTotal: 0,
      }
    );
  }, [filtered]);

  const totalProfit = totals.totalSales - totals.totalCost;
  const roas = safeDivide(totals.totalSales, totals.totalCost);
  const cartRate = safeDivide(totals.cart, totals.ss);
  const checkoutRate = safeDivide(totals.checkout, totals.cart);
  const cvRate = safeDivide(totals.cv, totals.cart);

  const cpaCv = filtered.length ? totals.cpaCvTotal / filtered.length : safeDivide(totals.totalCost, totals.cv);
  const cpaCart = filtered.length ? totals.cpaCartTotal / filtered.length : safeDivide(totals.totalCost, totals.cart);
  const roasHealth = getRoasHealth(roas);

  const chartData = useMemo(
    () =>
      filtered.map((item) => ({
        label: new Date((item._dateMillis as number)).toLocaleDateString("ja-JP", { month: "numeric", day: "numeric" }),
        売上: Number(item.total_sales ?? 0),
        広告費: Number(item.total_cost ?? 0),
        カート: Number(item.cart ?? 0),
        カートCPA: Number(item.cpa_cart ?? 0),
      })),
    [filtered]
  );

  const rangeText = `${new Date(activeRange.start + JST_OFFSET_MS).toLocaleDateString("ja-JP")} - ${new Date(activeRange.endExclusive - ONE_DAY_MS + JST_OFFSET_MS).toLocaleDateString("ja-JP")}`;

  return (
    <div className="space-y-6">
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-6 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="admin-analysis-title whitespace-nowrap admin-text-heading leading-tight">パフォーマンスダッシュボード</h2>
            <p className="text-[clamp(0.72rem,1.1vw,1rem)] admin-text-body mt-1 whitespace-nowrap">売上・広告のパフォーマンスを表示します</p>
            <Link
              to="/admin/analysis/dailydata"
              className="inline-flex mt-3 items-center gap-2 px-3 sm:px-4 py-2 rounded-lg border border-slate-300 bg-white text-[clamp(0.7rem,1vw,0.95rem)] text-slate-700 hover:bg-slate-50 whitespace-nowrap"
            >
              <i className="ri-line-chart-line" />
              デイリーデータ詳細へ
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-xl p-1 min-w-0">
            {[
              ["7days", "7日間"],
              ["14days", "14日間"],
              ["30days", "30日間"],
              ["thisMonth", "今月"],
              ["lastMonth", "先月"],
              ["custom", "カスタム"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setPreset(key as PresetKey)}
                className={`px-3 py-1.5 text-[clamp(0.64rem,0.95vw,0.875rem)] rounded-lg whitespace-nowrap ${preset === key ? "admin-analysis-accent-bg text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >
                {label}
              </button>
            ))}
            <div className="ml-2 px-3 py-1 text-[clamp(0.64rem,0.95vw,0.875rem)] admin-text-body border-l border-slate-200 whitespace-nowrap">{rangeText}</div>
            <div className="flex items-center gap-2 pl-2 flex-wrap">
              <input
                type="date"
                value={customStartDate}
                onChange={(event) => {
                  setPreset("custom");
                  setCustomStartDate(event.target.value);
                }}
                className="px-2 py-1 text-[clamp(0.64rem,0.95vw,0.875rem)] border border-slate-200 rounded-lg whitespace-nowrap"
              />
              <span className="text-slate-400 text-[clamp(0.7rem,1.1vw,0.875rem)]">〜</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(event) => {
                  setPreset("custom");
                  setCustomEndDate(event.target.value);
                }}
                className="px-2 py-1 text-[clamp(0.64rem,0.95vw,0.875rem)] border border-slate-200 rounded-lg whitespace-nowrap"
              />
            </div>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 overflow-hidden">
            <h3 className="text-[clamp(0.75rem,1.1vw,0.875rem)] admin-text-body font-semibold">売上</h3>
            <p className="admin-analysis-kpi admin-text-heading mt-2 leading-tight whitespace-nowrap">{formatYen(totals.totalSales)}</p>
            <ul className="mt-6 space-y-2 text-[clamp(0.64rem,0.95vw,0.875rem)]">
              <li className="flex justify-between gap-2"><span className="admin-text-body shrink-0 whitespace-nowrap">Google</span><span className="font-semibold text-right whitespace-nowrap">{formatYen(totals.googleSales)} ({formatPercent0(safeDivide(totals.googleSales, totals.totalSales))})</span></li>
              <li className="flex justify-between gap-2"><span className="admin-text-body shrink-0 whitespace-nowrap">Yahoo</span><span className="font-semibold text-right whitespace-nowrap">{formatYen(totals.yahooSales)} ({formatPercent0(safeDivide(totals.yahooSales, totals.totalSales))})</span></li>
              <li className="flex justify-between gap-2"><span className="admin-text-body shrink-0 whitespace-nowrap">AF</span><span className="font-semibold text-right whitespace-nowrap">{formatYen(totals.afSales)} ({formatPercent0(safeDivide(totals.afSales, totals.totalSales))})</span></li>
              <li className="flex justify-between gap-2"><span className="admin-text-body shrink-0 whitespace-nowrap">その他</span><span className="font-semibold text-right whitespace-nowrap">{formatYen(totals.otherSales)} ({formatPercent0(safeDivide(totals.otherSales, totals.totalSales))})</span></li>
            </ul>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 overflow-hidden">
            <h3 className="text-[clamp(0.75rem,1.1vw,0.875rem)] admin-text-body font-semibold">広告費</h3>
            <p className="admin-analysis-kpi admin-text-heading mt-2 leading-tight whitespace-nowrap">{formatYen(totals.totalCost)}</p>
            <div className="mt-6 space-y-4 text-[clamp(0.64rem,0.95vw,0.875rem)]">
              {[
                ["Google広告", totals.googleCost],
                ["Yahooプロモーション", totals.yahooCost],
                ["AFネットワーク", totals.afCost],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="flex justify-between gap-2 mb-1"><span className="admin-text-body min-w-0 truncate whitespace-nowrap">{label}</span><span className="font-semibold text-right whitespace-nowrap">{formatYen(Number(value))}</span></div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full admin-analysis-accent-bg rounded-full" style={{ width: `${Math.min(100, safeDivide(Number(value), totals.totalCost) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white border border-blue-200 rounded-2xl p-4 sm:p-5 shadow-sm lg:col-span-1 overflow-hidden">
            <h3 className="text-[clamp(0.75rem,1.1vw,0.875rem)] admin-text-body font-semibold">広告粗利</h3>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-[1fr_minmax(120px,180px)] gap-4 items-center">
              <div className="min-w-0">
                <p className="admin-analysis-kpi font-bold mt-2 leading-tight whitespace-nowrap admin-analysis-accent">{formatYen(totalProfit)}</p>
                <div className="mt-4 space-y-3 text-[clamp(0.64rem,0.95vw,0.875rem)]">
                  {[
                    ["Google広告", totals.googleProf],
                    ["Yahooプロモーション", totals.yahooProf],
                    ["AFネットワーク", totals.afProf],
                    ["その他", totals.otherProf],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div className="flex justify-between gap-2 mb-1"><span className="admin-text-body min-w-0 truncate whitespace-nowrap">{label}</span><span className="font-semibold text-right whitespace-nowrap">{formatYen(Number(value))}</span></div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full admin-analysis-accent-bg rounded-full" style={{ width: `${Math.min(100, safeDivide(Number(value), Math.max(totalProfit, 1)) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-center min-w-0">
                <div className="relative h-[clamp(6.5rem,12vw,9rem)] w-[clamp(6.5rem,12vw,9rem)]">
                  <svg viewBox="0 0 100 100" className="h-[clamp(6.5rem,12vw,9rem)] w-[clamp(6.5rem,12vw,9rem)] -rotate-90">
                    <circle cx="50" cy="50" r="40" stroke="#c4c4c4" strokeWidth="10" fill="none" />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke={roasHealth.color}
                      strokeWidth="10"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - roasHealth.progress)}`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-[clamp(0.75rem,1.2vw,1rem)] font-bold text-slate-600">ROAS</p>
                    <p className="text-[clamp(1.45rem,2.8vw,2.3rem)] font-extrabold leading-none" style={{ color: roasHealth.color }}>{Math.round(roas * 100)}</p>
                    <p className="text-[clamp(0.75rem,1.2vw,1rem)] font-bold leading-none mt-1" style={{ color: roasHealth.color }}>%</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="bg-slate-50 rounded-2xl p-3 sm:p-4 text-center overflow-hidden"><p className="text-[clamp(0.64rem,0.95vw,0.875rem)] font-semibold admin-text-body">CV CPA</p><p className="text-[clamp(0.82rem,1.3vw,1.1rem)] font-bold whitespace-nowrap">{formatYen(cpaCv)}</p></div>
              <div className="bg-slate-50 rounded-2xl p-3 sm:p-4 text-center overflow-hidden"><p className="text-[clamp(0.64rem,0.95vw,0.875rem)] font-semibold admin-text-body">カートCPA</p><p className="text-[clamp(0.82rem,1.3vw,1.1rem)] font-bold whitespace-nowrap">{formatYen(cpaCart)}</p></div>
            </div>
          </section>
        </div>

        <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 overflow-hidden">
          <div className="flex items-center justify-between">
            <h3 className="admin-analysis-section-title admin-text-heading whitespace-nowrap">ファネル分析</h3>
            <span className="text-[clamp(0.64rem,0.95vw,0.875rem)] admin-text-body text-right whitespace-nowrap">セッションからの成約フロー</span>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-4 items-end">
            <div className="space-y-2">
              <div className="h-44 bg-slate-50 border border-slate-200 rounded-xl flex flex-col items-center justify-center px-2 overflow-hidden">
                <p className="text-[clamp(0.68rem,1vw,0.75rem)] admin-text-body">セッション</p>
                <p className="text-[clamp(1.4rem,2.6vw,2.3rem)] font-bold text-slate-900 whitespace-nowrap text-center">{Math.round(Number(totals.ss)).toLocaleString("ja-JP")}</p>
              </div>
              <p className="text-center text-[clamp(0.64rem,0.95vw,0.875rem)] text-slate-600">セッション</p>
            </div>

            {(() => {
              const funnelSteps = [
                ["カート追加", totals.cart],
                ["決済開始", totals.checkout],
                ["CV (購入完了)", totals.cv],
              ] as const;
              const cartBase = Math.max(Number(totals.cart || 0), 1);

              return funnelSteps.map(([label, value], index) => (
                <div key={label} className="space-y-2">
                  <div className="h-44 bg-slate-100 rounded-xl flex items-end overflow-hidden">
                    <div
                      className="w-full bg-blue-500 text-white text-center font-semibold py-2 text-[clamp(0.64rem,0.95vw,0.875rem)] leading-tight whitespace-nowrap"
                      style={{
                        height: `${Math.max(8, (Number(value) / cartBase) * 100)}%`,
                        opacity: index === 0 ? 0.65 : 0.8,
                      }}
                    >
                      {Math.round(Number(value)).toLocaleString("ja-JP")}
                    </div>
                  </div>
                  <p className="text-center text-[clamp(0.64rem,0.95vw,0.875rem)] text-slate-600">{label}</p>
                </div>
              ));
            })()}
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <div className="bg-slate-50 rounded-xl p-3 overflow-hidden"><p className="text-[clamp(0.62rem,0.9vw,0.75rem)] admin-text-body whitespace-nowrap">カート遷移率</p><p className="text-[clamp(1.4rem,2.6vw,2.3rem)] font-bold whitespace-nowrap">{formatPercent(cartRate)}</p></div>
            <div className="bg-slate-50 rounded-xl p-3 overflow-hidden"><p className="text-[clamp(0.62rem,0.9vw,0.75rem)] admin-text-body whitespace-nowrap">CO遷移率</p><p className="text-[clamp(1.4rem,2.6vw,2.3rem)] font-bold whitespace-nowrap">{formatPercent(checkoutRate)}</p></div>
            <div className="bg-slate-50 rounded-xl p-3 overflow-hidden"><p className="text-[clamp(0.62rem,0.9vw,0.75rem)] admin-text-body whitespace-nowrap">CV遷移率(カート)</p><p className="text-[clamp(1.4rem,2.6vw,2.3rem)] font-bold whitespace-nowrap">{formatPercent(cvRate)}</p></div>
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="admin-analysis-section-title admin-text-heading whitespace-nowrap">売上 vs. 広告費 推移</h3>
              <p className="text-[clamp(0.64rem,0.95vw,0.875rem)] admin-text-body">日次パフォーマンス・トレンド</p>
            </div>
            <div className="flex items-center gap-4 text-[clamp(0.64rem,0.95vw,0.875rem)]">
              <span className="inline-flex items-center gap-2 whitespace-nowrap"><i className="h-2 w-2 rounded-full admin-analysis-accent-bg inline-block" />売上</span>
              <span className="inline-flex items-center gap-2 whitespace-nowrap"><i className="h-2 w-2 rounded-full bg-indigo-200 inline-block" />広告費</span>
            </div>
          </div>
          <div className="mt-4 h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} />
                <Tooltip formatter={(value: number) => formatYen(Number(value))} />
                <Line type="monotone" dataKey="売上" stroke="#2563eb" strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="広告費" stroke="#c7d2fe" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="admin-analysis-section-title admin-text-heading whitespace-nowrap">カート vs. カートCPA 推移</h3>
              <p className="text-[clamp(0.64rem,0.95vw,0.875rem)] admin-text-body">日次パフォーマンス・トレンド</p>
            </div>
            <div className="flex items-center gap-4 text-[clamp(0.64rem,0.95vw,0.875rem)]">
              <span className="inline-flex items-center gap-2 whitespace-nowrap"><i className="h-2 w-2 rounded-full admin-analysis-accent-bg inline-block" />カート</span>
              <span className="inline-flex items-center gap-2 whitespace-nowrap"><i className="h-2 w-2 rounded-full bg-indigo-200 inline-block" />カートCPA</span>
            </div>
          </div>
          <div className="mt-4 h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12, fill: "#94a3b8" }} tickFormatter={(value: number) => Math.round(value).toLocaleString("ja-JP")} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: "#94a3b8" }} tickFormatter={(value: number) => Math.round(value).toLocaleString("ja-JP")} />
                <Tooltip formatter={(value: number) => Math.round(Number(value)).toLocaleString("ja-JP")} />
                <Line yAxisId="left" type="monotone" dataKey="カート" stroke="#2563eb" strokeWidth={3} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="カートCPA" stroke="#c7d2fe" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      {loading && <p className="text-sm admin-text-body">読み込み中...</p>}
    </div>
  );
}
